import { api } from 'api';
import WarnningIcon from 'assets/icons/warnning-red-icon.svg';
import FormInputValidation from 'components/FormForgotPassword/components/FormInputValidation';
import Introduction from 'components/FormLogIn/components/Introduction';
import FormResetPassword from 'components/FormResetPassword';
import {
  checkFormInputValidation,
  checkTokenExpired,
} from 'components/FormResetPassword/func';
import FormResetPasswordMobile from 'components/FormResetPassword/mobile';
import {
  DESKTOP_MIN_WIDTH,
  PASSWORD_LIVE_CHECKS,
  RESET_PASSWORD_FIELDS,
  RESET_PASSWORD_INFORMATION,
  RESET_PASSWORD_PASSWORD_NAME,
} from 'constants';
import { checkMaintenance } from 'helpers/maintenance';
import useForm from 'hooks/form/useForm';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import useQuery from 'hooks/useQuery';
import useStore from 'hooks/useStore';
import { useEffect, useState } from 'react';
import { SET_SHOW_LOADING } from 'store/action';
import styled from 'styled-components';

const ResetPasswordPage = () => {
  const { t } = useLang();
  const query = useQuery();
  const token = query.get('token');
  const { dispatch } = useStore();
  const { isMobile, isTablet } = useDetectDevice();

  const {
    information,
    setInformation,
    validation,
    setValidation,
    checkAllowSubmit,
  } = useForm(RESET_PASSWORD_INFORMATION);

  const [isResetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [isResetPasswordExpired, setResetPasswordExpired] = useState(false);
  const [liveCheckStatus, setLiveCheckStatus] = useState({
    0: WarnningIcon,
    1: WarnningIcon,
    2: WarnningIcon,
    3: WarnningIcon,
  });

  useEffect(() => {
    if (checkTokenExpired(token)) {
      setResetPasswordExpired(true);
    }
  }, [token]);

  const handleSubmit = async () => {
    if (checkTokenExpired(token)) {
      setResetPasswordExpired(true);
      return;
    }

    let newInformation = { ...information };
    let newValidation = { ...validation };

    RESET_PASSWORD_FIELDS.forEach((field) => {
      newValidation = checkFormInputValidation(
        information[field.name],
        field,
        newInformation,
        newValidation
      );
    });

    setValidation(newValidation);

    const isAllowSubmit = checkAllowSubmit({
      information,
      validation: newValidation,
    });

    if (isAllowSubmit) {
      dispatch({ type: SET_SHOW_LOADING, payload: true });
      try {
        const { data } = await api.resetPassword(
          query.get('token'),
          information[RESET_PASSWORD_PASSWORD_NAME]
        );
        if (data?.success) {
          setResetPasswordSuccess(true);
          dispatch({ type: SET_SHOW_LOADING, payload: false });
        }
      } catch (error) {
        checkMaintenance(error);
        console.error(error?.message);
        dispatch({ type: SET_SHOW_LOADING, payload: false });
      }
    }
  };

  const renderPasswordRequires = () => (
    <PasswordRequires>
      {PASSWORD_LIVE_CHECKS.map((check, index) => (
        <li key={check.id}>
          <img src={liveCheckStatus[index]} alt="" />
          <Paragraph>{t(check.require)}</Paragraph>
        </li>
      ))}
    </PasswordRequires>
  );

  const renderFormInputValidations = () => (
    <>
      {RESET_PASSWORD_FIELDS.map((field) => (
        <FormInputValidation
          key={field.name}
          field={field}
          information={information}
          setInformation={setInformation}
          validation={validation}
          setValidation={setValidation}
          setLiveCheckStatus={setLiveCheckStatus}
        />
      ))}
    </>
  );

  if (isMobile || isTablet) {
    return (
      <ResetPasswordStyled>
        <FormResetPasswordMobile
          isResetPasswordSuccess={isResetPasswordSuccess}
          isResetPasswordExpired={isResetPasswordExpired}
          handleSubmit={handleSubmit}
          renderPasswordRequires={renderPasswordRequires}
          renderFormInputValidations={renderFormInputValidations}
        />
      </ResetPasswordStyled>
    );
  }
  return (
    <ResetPasswordStyled>
      <Introduction />
      <FormWrap>
        <FormResetPassword
          isResetPasswordSuccess={isResetPasswordSuccess}
          isResetPasswordExpired={isResetPasswordExpired}
          handleSubmit={handleSubmit}
          renderPasswordRequires={renderPasswordRequires}
          renderFormInputValidations={renderFormInputValidations}
        />
      </FormWrap>
    </ResetPasswordStyled>
  );
};

const ResetPasswordStyled = styled.div`
  padding: 24px;
  padding-top: 94px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    padding: 0;
    display: grid;
    grid-template-columns: 55% 45%;
    align-items: end;
    margin-block: auto;
  }

  @media screen and (min-width: 1440px) {
    grid-template-columns: 60% 40%;
    margin-block: 112px auto;
  }

  @media screen and (min-width: 2560px) {
    margin-block: 148px auto;
  }
`;

const FormWrap = styled.div`
  background: transparent;

  @media screen and (min-width: 1280px) {
    width: 485px;
    height: 558px;
  }

  @media screen and (min-width: 1440px) {
    width: 517px;
    height: 659px;
  }
`;

const PasswordRequires = styled.ul`
  margin: 0;
  margin-bottom: 24px;
  padding-left: 0px;

  & li {
    display: flex;
    align-items: center;
    list-style: disc;
    list-style-type: disc;

    & img {
      margin-right: 8px;
    }

    & p {
      margin-bottom: 0px;
    }
  }
`;

const Paragraph = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;

  color: var(--ds-c-grey-dark);
  margin-bottom: 8px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

export default ResetPasswordPage;
